/**
 *
 * @Copyright 2024 UNLOCKIT DECENTRALIZATION, LDA
 * Development by VOID Software, SA
 *
 */

import { useNavigate, useParams } from 'react-router-dom';
import { FunctionComponent, useEffect, useState } from 'react';
import { WorkflowContext, withWorkflowContext } from '../../controllers/WorkflowContext';
import { LoadingScreen } from '../LoadingScreen';
import { DefaultLayout } from '../../elements/layouts/DefaultLayout';
import { AppRoute } from '../../../constants/routes';
import { ErrorResponse } from '../../../types/errors';
import { buildUrl } from '../../../utils/navigation';
import { Workflow } from '../../../types/workflows';

type OwnProps = WorkflowContext;

const ParticipantAssignScreenBase: FunctionComponent<OwnProps> = (props) => {
    const {
        loadWorkflowInformation,
        assignUserToParticipant,
    } = props;
    const { participantToken = '' } = useParams();
    const navigate = useNavigate();

    const [isVerifying, setIsVerifying] = useState(true);
    const [errorResponse, setErrorResponse] = useState<ErrorResponse | null>(null);
    
    useEffect(() => {
        handleInit();
    }, []);

    useEffect(() => {
        if (errorResponse) throw errorResponse;
    }, [errorResponse]);

    /**
     * Component initialization logic
     */
    const handleInit = async () => {
        if (participantToken) {
            const response = await loadWorkflowInformation(participantToken);
            prepareAssignUserToParticipant(response[0]);
        }
    };
    const prepareAssignUserToParticipant = async (requestedWorkflow: Workflow | null) => {
        const [assignedParticipant, assignParticipantError] = await assignUserToParticipant(participantToken);
        setIsVerifying(false);

        if (assignParticipantError) {
            switch (Number(assignParticipantError.errors[0].errorCode)) {
                // user email is different and a pin will be sent
                case 81:
                    navigate(buildUrl(AppRoute.WorkflowParticipantAssignPinValidation, { participantToken }));
                    return;
                // participant was already assigned
                case 79:
                    navigate(buildUrl(AppRoute.ViewWorkflow, { workflowId: String(requestedWorkflow?.id) }));
                    return;
                default:
                    setErrorResponse(assignParticipantError);
                    return;
            }
        }
        navigate(buildUrl(AppRoute.ViewWorkflow, { workflowId: String(requestedWorkflow?.id ?? assignedParticipant.transactionId) }));
    };

    return (
        <DefaultLayout>
            {isVerifying && <LoadingScreen />}
        </DefaultLayout>
    );
};

export const ParticipantAssignScreen = withWorkflowContext(ParticipantAssignScreenBase);
